import React, { useMemo, useState } from 'react'
import { Modal, Button, Alert, Spinner, Form } from 'react-bootstrap'
import axios from 'axios'

const parseCsvLine = (line, separator) => {
  const result = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i]

    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"'
        i += 1
      } else {
        inQuotes = !inQuotes
      }
      continue
    }

    if (char === separator && !inQuotes) {
      result.push(current)
      current = ''
      continue
    }

    current += char
  }

  result.push(current)
  return result
}

const parseCsvContent = text => {
  const cleaned = text.replace(/^\uFEFF/, '')
  const lines = cleaned
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean)

  if (lines.length < 2) {
    return { headers: [], rows: [] }
  }

  const separator = lines[0].includes(';') ? ';' : ','
  const headers = parseCsvLine(lines[0], separator).map(h => h.trim())
  const rows = []

  for (let i = 1; i < lines.length; i += 1) {
    const values = parseCsvLine(lines[i], separator)
    if (values.length === 0) continue

    const row = {}
    headers.forEach((header, idx) => {
      row[header] = (values[idx] || '').trim()
    })
    rows.push(row)
  }

  return { headers, rows }
}

const parseHrfNumber = value => {
  if (value === null || value === undefined) return null
  const normalized = String(value).trim().replace(',', '.')
  if (!normalized) return null
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

const combineSkillWithSub = (baseValue, subValue) => {
  const base = parseHrfNumber(baseValue)
  if (base === null) return ''
  const sub = parseHrfNumber(subValue)
  if (sub === null) return base
  return base + sub
}

const parseHrfContent = text => {
  const cleaned = text.replace(/^\uFEFF/, '')
  const lines = cleaned.split(/\r?\n/)

  let currentSection = null
  let currentData = {}
  const sections = []

  const pushCurrentSection = () => {
    if (!currentSection) return
    sections.push({ section: currentSection, data: currentData })
  }

  for (const rawLine of lines) {
    const line = rawLine.trim()
    if (!line) continue

    const sectionMatch = line.match(/^\[(.+)\]$/)
    if (sectionMatch) {
      pushCurrentSection()
      currentSection = sectionMatch[1]
      currentData = {}
      continue
    }

    if (!currentSection) continue

    const sepIndex = line.indexOf('=')
    if (sepIndex < 0) continue

    const key = line.slice(0, sepIndex).trim()
    const value = line.slice(sepIndex + 1).trim()
    currentData[key] = value
  }

  pushCurrentSection()

  const basics = sections.find(s => s.section.toLowerCase() === 'basics')?.data
  const fallbackLeagueId = basics?.leagueID || ''
  const fallbackUpdated = basics?.date || ''

  const rows = sections
    .filter(s => /^player\d{9}$/i.test(s.section))
    .map(({ section, data }) => {
      const playerId = section.replace(/^player/i, '')

      return {
        PlayerID: playerId,
        FirstName: data.firstname || '',
        NickName: data.nickname || '',
        LastName: data.lastname || '',
        Age: data.ald || '',
        AgeDays: data.agedays || '',
        Salary: data.sal || '',
        NativeLeagueID: data.NativeLeagueID || fallbackLeagueId,
        OwningLeagueID: data.OwningLeagueID || fallbackLeagueId,
        UpdatedSkills: data.UpdatedSkills || data.Updated || fallbackUpdated,
        Specialty: data.speciality || data.Speciality || '',
        StaminaSkill: combineSkillWithSub(data.uth, data.uthSub),
        KeeperSkill: combineSkillWithSub(data.mlv, data.mlvSub),
        PlaymakerSkill: combineSkillWithSub(data.spe, data.speSub),
        ScorerSkill: combineSkillWithSub(data.mal, data.malSub),
        PassingSkill: combineSkillWithSub(data.fra, data.fraSub),
        WingerSkill: combineSkillWithSub(data.ytt, data.yttSub),
        DefenderSkill: combineSkillWithSub(data.bac, data.bacSub),
        SetPiecesSkill: combineSkillWithSub(data.fas, data.fasSub),
      }
    })

  return { rows }
}

const CandidateCsvSyncModal = ({ show, onHide, candidateId, onSynced }) => {
  const [fileName, setFileName] = useState('')
  const [rows, setRows] = useState([])
  const [isDragging, setIsDragging] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [summary, setSummary] = useState(null)

  const uniquePlayerCount = useMemo(() => {
    const ids = new Set(
      rows
        .map(row => (row.PlayerID || '').trim())
        .filter(id => /^\d+$/.test(id))
    )
    return ids.size
  }, [rows])

  const resetState = () => {
    setFileName('')
    setRows([])
    setIsDragging(false)
    setIsSubmitting(false)
    setError('')
    setSuccess('')
    setSummary(null)
  }

  const handleClose = () => {
    resetState()
    onHide()
  }

  const handleFile = async file => {
    if (!file) return

    const lowerName = file.name.toLowerCase()
    if (
      !lowerName.endsWith('.csv') &&
      !lowerName.endsWith('.cvs') &&
      !lowerName.endsWith('.hrf')
    ) {
      setError('Valitse .csv, .cvs tai .hrf tiedosto.')
      return
    }

    try {
      setError('')
      const text = await file.text()
      const { rows: parsedRows } = lowerName.endsWith('.hrf')
        ? parseHrfContent(text)
        : parseCsvContent(text)

      if (!parsedRows.length) {
        setError('Tiedostosta ei löytynyt rivejä.')
        setRows([])
        return
      }

      setFileName(file.name)
      setRows(parsedRows)
      setSummary(null)
      setSuccess('Tiedosto luettu onnistuneesti.')
    } catch (err) {
      setError('Tiedoston luku epäonnistui.')
      setRows([])
    }
  }

  const onFileInputChange = event => {
    const file = event.target.files?.[0]
    handleFile(file)
    event.target.value = ''
  }

  const onDrop = event => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)

    const file = event.dataTransfer.files?.[0]
    handleFile(file)
  }

  const onDragOver = event => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(true)
  }

  const onDragLeave = event => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragging(false)
  }

  const handleSync = async () => {
    if (!candidateId) {
      setError('Listan tunniste puuttuu.')
      return
    }

    if (!rows.length) {
      setError('Lataa ensin CSV/HRF tiedosto.')
      return
    }

    setIsSubmitting(true)
    setError('')
    setSuccess('')

    try {
      const loggedUser = window.localStorage.getItem('loggedUser')
      const token = loggedUser ? JSON.parse(loggedUser) : null

      const response = await axios.post(
        `/api/candidates/${candidateId}/sync-csv-skills`,
        { rows },
        { headers: { 'x-oauth-token': token } }
      )

      setSummary(response.data)
      setSuccess('CSV/HRF-synkronointi valmis.')

      if (onSynced) {
        await onSynced(response.data)
      }

      handleClose()
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.message ||
        'CSV/HRF-synkronointi epäonnistui.'
      setError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header
        closeButton
        style={{ backgroundColor: '#23272A', color: '#FFFFFF' }}
      >
        <Modal.Title>CSV/HRF taitojen synkronointi</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: '#2C2F33', color: '#FFFFFF' }}>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          style={{
            border: isDragging ? '2px solid #2ecc71' : '2px dashed #5d6269',
            borderRadius: '10px',
            padding: '24px',
            textAlign: 'center',
            backgroundColor: '#1E2124',
            marginBottom: '16px',
          }}
        >
          <div style={{ marginBottom: '8px' }}>
            Raahaa CSV/HRF tiedosto tähän
          </div>
          <div style={{ color: '#aaa', marginBottom: '10px' }}>
            tai valitse tiedosto levyltä
          </div>
          <Form.Control
            type="file"
            accept=".csv,.cvs,.hrf"
            onChange={onFileInputChange}
            style={{ maxWidth: '320px', margin: '0 auto' }}
          />
        </div>

        {fileName && (
          <div style={{ color: '#b9bec5', marginBottom: '10px' }}>
            Tiedosto: {fileName}
          </div>
        )}

        <div style={{ color: '#b9bec5', marginBottom: '8px' }}>
          Rivit: {rows.length} | Uniikit PlayerID:t: {uniquePlayerCount}
        </div>

        {summary && (
          <div
            style={{
              backgroundColor: '#1E2124',
              borderRadius: '8px',
              padding: '12px',
              fontSize: '0.95rem',
            }}
          >
            <div>Käsitelty: {summary.processedRows || 0}</div>
            <div>
              Päivitetty taidoiltaan: {summary.updatedSkillPlayers || 0}
            </div>
            <div>Lisätty listalle: {summary.addedToList || 0}</div>
            <div>Luotu tietokantaan: {summary.addedToDatabase || 0}</div>
            <div>Puuttuva PlayerID: {summary.invalidRows || 0}</div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer
        style={{ backgroundColor: '#23272A', borderTop: '1px solid #444' }}
      >
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Sulje
        </Button>
        <Button
          variant="success"
          onClick={handleSync}
          disabled={!rows.length || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Synkronoidaan...
            </>
          ) : (
            'Synkronoi CSV/HRF'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default CandidateCsvSyncModal
